import { FontIcon } from "@fluentui/react";
import {
  OpenRegular,
  ChatMultipleRegular,
  AttachRegular,
  MoreHorizontalRegular,
  FolderArrowRightRegular,
  BriefcaseRegular,
  DismissRegular,
} from "@fluentui/react-icons";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Tooltip,
} from "@fluentui/react-components";
import { getMessageSummary, getConversationSummary } from "../../../api/follow";
import { markAsReadRequest } from "../../../api/readMessages";
import { fileItems } from "../../../api/file";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import useAuthContext from "../../../hooks/useAuthContext";
import { useLocation, useBlocker } from "react-router-dom";
import { formatDate, getFolderPathDisplaySegments, getFiledFullPath, wrapAtSlashes } from "../../../utils";
import FullPageLoader from "../../../components/loaders/FullPageLoader";
import {
  contentWarnClass,
  refreshBtnClass,
  attachIconClass,
  bubbleFooterClass,
  openIconClass,
  avatarRowClass,
  expandIconClass,
  underlineStyle,
  summaryBodyClass,
  messageBubbleClass,
  messageItemClass,
  stackColumnGap10,
  emptyStateClass,
  dateClass,
  nameClass,
  dateNameContainerClass,
  stackContainerClass,
  pageContainerClass,
  filedFolderClass,
  filedFolderLabelClass,
  filedMetaClass,
  fileReadBtnRowClass,
  unreadTextClass,
  unreadShortSummaryClass,
  clickableShortSummaryClass,
  inlineFilingPanelClass,
  filingPanelHeaderClass,
} from "./styles";
import useOfficeContext from "../../../hooks/useOfficeContext";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { useEffect, useState, useCallback } from "react";
import React from "react";
import PageHeader from "../../../components/Headings/PageHeader";
import { useMount } from "react-use";
import { messagesSummary, suggestedFolder } from "../../../types/following";
import { useFilingContext } from "../../../contexts/FilingContext";
// import { getMockSuggestions } from "../../../mockFilingSuggestions";
import Filing from "../../../components/Filing";
import CircularCheckbox from "./CircularCheckbox";
import FiledFolderPath from "./FiledFolderPath";

const MessageSummary = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [isLongSummaryVisible, setIsLongSummaryVisible] = useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const messageRefs = React.useRef<(HTMLDivElement | null)[]>([]);

  const [selectedMessageIds, setSelectedMessageIds] = useState<string[]>([]);
  const [isFiling, setIsFiling] = useState(false);
  const [showFileBrowse, setShowFileBrowse] = useState(false);

  const { suggestedFolders, setSuggestedFolders, setLastFiledFolder } = useFilingContext();

  const { tokenData } = useAuthContext();
  const { itemIds, isMobile } = useOfficeContext();
  const { state: locationState }: any = useLocation();
  const msUserId = tokenData.oid;
  const conversationId = locationState?.conversationId;
  const queryClient = useQueryClient();

  const queryKey = conversationId
    ? ["conversationSummaryQuery", conversationId]
    : ["messageSummaryQuery", itemIds[0]];

  const queryFn = conversationId
    ? () => getConversationSummary(msUserId, [conversationId])
    : () => getMessageSummary(msUserId, [itemIds[0]]);

  const { data, isPending, refetch, isError, error } = useQuery({
    queryKey,
    queryFn,
    enabled: !!msUserId && !!itemIds.length,
    staleTime: 0,
  });

  const blocker = useBlocker(selectedMessageIds.length > 0);

  useEffect(() => {
    setShowFileBrowse(false);
    setSelectedMessageIds([]);
  }, [itemIds[0]]);

  useEffect(() => {
    if (!containerRef.current) return;
    const firstUnreadIndex = data?.messages.findIndex((m) => !m.isRead) ?? -1;
    const firstUnreadEl = firstUnreadIndex !== -1 ? messageRefs.current[firstUnreadIndex] : null;
    if (firstUnreadEl) {
      firstUnreadEl.scrollIntoView({ block: "start" });
    } else {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [data]);

  const markAsRead = useCallback(
    (messageId: string | undefined) => {
      if (!messageId) return;
      const message = data?.messages.find((m) => m.id === messageId);
      if (!message || message.isRead) return;
      queryClient.setQueryData(queryKey, (old: messagesSummary) => {
        if (!old) return old;
        return {
          ...old,
          messages: old.messages.map((m) => (m.id === messageId ? { ...m, isRead: true } : m)),
        };
      });
      markAsReadRequest(msUserId, [messageId]).catch(() => {
        queryClient.setQueryData(queryKey, (old: messagesSummary) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((m) => (m.id === messageId ? { ...m, isRead: false } : m)),
          };
        });
      });
      setSelectedMessageIds((prev) => {
        if (prev.indexOf(messageId) !== -1) return prev;
        const next = [...prev, messageId];
        if (next.length === 1) {
          const msg = data?.messages.find((m) => m.id === messageId);
          const folders = msg?.suggestedFolders ?? [];
          if (folders.length > 0) {
            setSuggestedFolders(folders);
          } else {
            // getMockSuggestions(msg?.id).then(setSuggestedFolders);
            setSuggestedFolders([]);
          }
        }
        return next;
      });
    },
    [data, msUserId, queryClient, queryKey, setSuggestedFolders]
  );

  const toggleSelected = useCallback(
    (messageId: string) => {
      const message = data?.messages.find((m) => m.id === messageId);
      const isCurrentlySelected = selectedMessageIds.includes(messageId);

      if (!isCurrentlySelected && message && !message.isRead && message.longSummary.length === 0) {
        markAsRead(messageId);
        return;
      }

      setSelectedMessageIds((prev) => {
        const isNowSelected = !prev.includes(messageId);
        if (!isNowSelected) return prev.filter((id) => id !== messageId);
        const next = [...prev, messageId];
        if (next.length === 1) {
          const folders = message?.suggestedFolders ?? [];
          if (folders.length > 0) {
            setSuggestedFolders(folders);
          } else {
            // getMockSuggestions(message?.id).then(setSuggestedFolders);
            setSuggestedFolders([]);
          }
        }
        return next;
      });
    },
    [data, selectedMessageIds, markAsRead, setSuggestedFolders]
  );

  const handleFiledSuccess = useCallback(
    async (folder: suggestedFolder) => {
      setLastFiledFolder(null);
      await markAsReadRequest(msUserId, selectedMessageIds);
      queryClient.setQueryData(queryKey, (old: messagesSummary) => {
        if (!old) return old;
        return {
          ...old,
          messages: old.messages.map((m) =>
            selectedMessageIds.includes(m.id)
              ? { ...m, filed: folder, isRead: true }
              : m
          ),
        };
      });
      setSelectedMessageIds([]);
      setShowFileBrowse(false);
    },
    [msUserId, selectedMessageIds, queryClient, queryKey, setLastFiledFolder]
  );

  const handleFileConversation = useCallback(async () => {
    const msgs = data?.messages ?? [];
    const allIds = msgs.map((m) => m.id);
    if (!allIds.length) return;
    const convFolders = msgs[0]?.suggestedFolders ?? [];
    setSuggestedFolders(convFolders /* .length > 0 ? convFolders : await getMockSuggestions(msgs[0]?.id) */);
    const folder = msgs.find((m) => m.filed && msgs.some((n) => !n.filed))?.filed ?? null;
    if (folder) {
      setIsFiling(true);
      try {
        await fileItems(msUserId, allIds, folder.graphFolderId);
        setLastFiledFolder(null);
        await markAsReadRequest(msUserId, allIds);
        queryClient.setQueryData(queryKey, (old: messagesSummary) => {
          if (!old) return old;
          return {
            ...old,
            messages: old.messages.map((m) =>
              allIds.includes(m.id) ? { ...m, filed: folder, isRead: true } : m
            ),
          };
        });
        setSelectedMessageIds([]);
        setShowFileBrowse(false);
      } finally {
        setIsFiling(false);
      }
    } else {
      setSelectedMessageIds(allIds);
      setShowFileBrowse(true);
    }
  }, [data, msUserId, setSuggestedFolders, setLastFiledFolder, queryClient, queryKey]);

  const handleExpandClick = (index) => {
    if (expandedIndex === index) {
      if (!isLongSummaryVisible) {
        setIsLongSummaryVisible(true);
        markAsRead(data?.messages[index]?.id);
      } else {
        setIsLongSummaryVisible(false);
      }
    } else {
      setExpandedIndex(index);
      setIsLongSummaryVisible(true);
      markAsRead(data?.messages[index]?.id);
    }
  };

  useEffect(() => {
    if (expandedIndex !== null && isLongSummaryVisible) {
      messageRefs.current[expandedIndex]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [expandedIndex, isLongSummaryVisible]);

  // Split into one sentence per paragraph; rendered as text so React escapes it.
  const splitIntoParagraphs = (text: string): string[] =>
    text.replace(/[.!?]+\s+(?=[A-Z])/g, ".\n").split("\n");

  const appInsights = useAppInsightsContext();
  useMount(() => {
    appInsights.trackPageView({ name: "Changes summary" });
  });

  if (isPending) return <FullPageLoader title="Summary loading..." />;

  if (isError || !data)
    return (
      <div className={emptyStateClass}>
        {isError
          ? (error as Error)?.message ?? "Failed to load summary."
          : "No summary available."}
      </div>
    );

  const hasSelectedPending = selectedMessageIds.length > 0;

  return (
    <div className={pageContainerClass}>
      <PageHeader headingText={data.subject} icon={<ChatMultipleRegular />} />

      <div className={stackContainerClass} ref={containerRef}>
        <div className={stackColumnGap10}>
          {data.messages.map((message, index) => (
            <React.Fragment key={message.id}>
              <div className={messageItemClass(message.internalSender)} ref={(el) => { messageRefs.current[index] = el; }}>
                <div className={messageBubbleClass(message.internalSender, selectedMessageIds.includes(message.id))}>
                  <div className={avatarRowClass}>
                    <CircularCheckbox
                      name={message.name}
                      checked={selectedMessageIds.includes(message.id)}
                      onChange={() => toggleSelected(message.id)}
                    />
                    <div className={dateNameContainerClass}>
                      <div className={`${nameClass}${!message.isRead ? ` ${unreadShortSummaryClass}` : ''}`}>{message.name}</div>
                      <div className={`${dateClass}${!message.isRead ? ` ${unreadShortSummaryClass}` : ''}`}>{formatDate(new Date(message.receivedDateTime))}</div>
                    </div>
                  </div>

                  <div className={summaryBodyClass}>
                    <p
                      className={[
                        !message.isRead ? unreadShortSummaryClass : undefined,
                        message.longSummary.length === 0 ? clickableShortSummaryClass : undefined,
                      ].filter(Boolean).join(' ') || undefined}
                      onClick={message.longSummary.length === 0 ? () => {
                        markAsRead(message.id);
                        if (message.isRead) toggleSelected(message.id);
                      } : undefined}
                    >
                      {message.shortSummary}
                    </p>
                    {message.longSummary.length > 0 && (
                      <>
                        {expandedIndex === index && isLongSummaryVisible && (
                          <div className={underlineStyle} />
                        )}
                        {expandedIndex === index && isLongSummaryVisible && (
                          <p
                            className={!message.isRead ? unreadTextClass : undefined}
                            onClick={() => setIsLongSummaryVisible(false)}
                          >
                            {splitIntoParagraphs(message.longSummary).map((paragraph, i) => (
                              <React.Fragment key={i}>
                                {i > 0 && (
                                  <>
                                    <br />
                                    <br />
                                  </>
                                )}
                                {paragraph}
                              </React.Fragment>
                            ))}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {(message.hasAttachments || message.filed) && (
                    <div className={bubbleFooterClass}>
                      {message.hasAttachments && <AttachRegular className={attachIconClass} />}
                      {message.filed && (
                        <div className={filedMetaClass}>
                          {message.filed.workspaceName && (
                            <Tooltip
                              content={
                                message.filed.workspaceFolderName
                                  ? `${message.filed.workspaceName}/${message.filed.workspaceFolderName}`
                                  : message.filed.workspaceName
                              }
                              relationship="label"
                            >
                              <span className={filedFolderClass}>
                                <BriefcaseRegular />
                                <span className={filedFolderLabelClass}>{message.filed.workspaceName}</span>
                              </span>
                            </Tooltip>
                          )}
                          {(() => {
                            const fullPath = getFiledFullPath(message.filed);
                            const segments = getFolderPathDisplaySegments(fullPath);
                            return (
                              <FiledFolderPath
                                segments={segments}
                                tooltipContent={wrapAtSlashes(fullPath)}
                              />
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {message.longSummary.length > 0 && (
                    <Button
                      appearance="subtle"
                      icon={<MoreHorizontalRegular />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExpandClick(index);
                      }}
                      className={expandIconClass}
                    />
                  )}

                  {!isMobile && message.id !== itemIds[0] && (
                    <Button
                      appearance="subtle"
                      icon={<OpenRegular />}
                      onClick={(e) => {
                        e.stopPropagation();
                        markAsRead(message.id);
                        Office.context.mailbox.displayMessageForm(message.id);
                      }}
                      className={openIconClass}
                    />
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>

      {!showFileBrowse && (
        <div className={fileReadBtnRowClass}>
          {hasSelectedPending && (
            <Button
              appearance="primary"
              icon={<FolderArrowRightRegular />}
              onClick={() => setShowFileBrowse(true)}
            >
              File Selected Messages
            </Button>
          )}
          <Button
            appearance={hasSelectedPending ? "secondary" : "primary"}
            icon={<FolderArrowRightRegular />}
            disabled={isFiling}
            onClick={handleFileConversation}
          >
            File Conversation
          </Button>
          <div className={contentWarnClass}>
            <Tooltip content="Regenerate summary" relationship="label">
              <Button
                appearance="subtle"
                icon={<FontIcon iconName="Refresh" />}
                onClick={() => {
                  queryClient.removeQueries({ queryKey });
                  refetch();
                }}
                className={refreshBtnClass}
              />
            </Tooltip>
            <span>AI generated message summary content may be incorrect. Click refresh to regenerate.</span>
          </div>
        </div>
      )}

      {showFileBrowse && (
        <div className={inlineFilingPanelClass}>
          <div className={filingPanelHeaderClass}>
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissRegular />}
              onClick={() => {
                setShowFileBrowse(false);
                setSelectedMessageIds([]);
              }}
              aria-label="Cancel filing"
            />
          </div>
          <Filing itemIds={selectedMessageIds} onSuccess={handleFiledSuccess} />
        </div>
      )}

      {blocker.state === "blocked" && (
        <Dialog open>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>File Selected Messages?</DialogTitle>
              <DialogContent>
                You have selected messages that haven't been filed yet. Do you want to file them before leaving?
              </DialogContent>
              <DialogActions>
                <Button
                  appearance="primary"
                  onClick={() => {
                    setShowFileBrowse(true);
                    blocker.reset();
                  }}
                >
                  Yes
                </Button>
                <Button
                  appearance="secondary"
                  onClick={() => {
                    setSelectedMessageIds([]);
                    blocker.proceed();
                  }}
                >
                  No
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
      )}
    </div>
  );
};

export default MessageSummary;
